import type { Data } from '@repo/strapi-types'
import { formatImageUrl } from '@/lib/strapi-helpers'
import type { StrapiImageMedia } from '@/types/api'
import type { StaticImport } from '@/types/next'

// Responsive sizes based on our design system breakpoints
// This is based on our upload config in strapi/config/plugin.ts > https://docs.strapi.io/cms/features/media-library#example-custom-configuration
export const DEFAULT_IMAGE_SIZES =
	'(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw' as const

// Format priority order (smallest to largest) - mobile-first
export const FORMAT_PRIORITY = ['xsmall', 'small', 'medium', 'large', 'xlarge'] as const

/**
 * Gets the next available format URL for a given target width
 * Falls back to smaller formats if larger ones aren't available
 */
export function getImageUrlByWidth(
	media: StrapiImageMedia,
	targetWidth?: number | `${number}${string}`,
): string | null {
	if (!media.url) {
		return null
	}

	if (targetWidth === undefined) {
		return formatAndValidateImageUrl(media.url)
	}

	const targetWidthNumber = Number(targetWidth)

	// Find the smallest format that meets or exceeds the target width
	const selectedFormat = FORMAT_PRIORITY.find((format) => {
		const formatData = media.formats?.[format]
		return formatData?.url && formatData.width && formatData.width >= targetWidthNumber
	})

	const url = selectedFormat ? media.formats?.[selectedFormat]?.url : null

	// Fallback to original url if no suitable format found
	return formatAndValidateImageUrl(url ?? media.url)
}

export function generateSrcset(media: StrapiImageMedia): string | undefined {
	if (!media.url) {
		return undefined
	}

	const srcset: string[] = []

	// Add available formats
	for (const format of FORMAT_PRIORITY) {
		const formatData = media.formats?.[format]
		if (formatData?.url && formatData.width) {
			const formattedUrl = formatImageUrl(formatData.url)

			if (isStringUrl(formattedUrl)) {
				srcset.push(`${formattedUrl} ${formatData.width}w`)
			}
		}
	}

	// Add original image as fallback
	const originalUrl = formatImageUrl(media.url)

	if (isStringUrl(originalUrl)) {
		srcset.push(`${originalUrl} ${media.width || 0}w`)
	}

	return srcset.join(', ')
}

function formatAndValidateImageUrl(url: string): string | null {
	const formattedUrl = formatImageUrl(url)
	return isStringUrl(formattedUrl) ? formattedUrl : null
}

function isStringUrl(url: string | StaticImport | null | undefined): url is string {
	return typeof url === 'string' && url.length > 0
}

export const getAspectRatioAttribute = (
	aspectRatio: Data.Component<'shared.video'>['aspectRatio'],
) => {
	switch (aspectRatio) {
		case 'aspect-1/1':
			return { aspectRatio: '1/1' }
		case 'aspect-16/9':
			return { aspectRatio: '16/9' }
		case 'aspect-9/16':
			return { aspectRatio: '9/16' }
		case 'aspect-4/3':
			return { aspectRatio: '4/3' }
		case 'aspect-3/4':
			return { aspectRatio: '3/4' }
		default:
			return { aspectRatio: '16/9' }
	}
}
